<template>
  <b-card title="Ajouter un ramasseur">
    <b-overlay
      :show="isLoading"
      rounded="lg"
      spinner-variant="primary"
    >
      <validation-observer
        ref="formValidator"
        #default="{ handleSubmit }"
      >
        <b-form @submit.prevent="handleSubmit(submitForm)">
          <b-card>
            <h5 class="mb-3">
              <feather-icon
                icon="UserIcon"
                class="mr-1"
              />
              Informations personnelles
            </h5>

            <b-row>
              <!-- Username -->
              <b-col md="6">
                <b-form-group>
                  <template #label>
                    Nom du ramasseur <span class="text-danger">*</span>
                  </template>
                  <validation-provider
                    #default="{ errors }"
                    name="Nom"
                    rules="required"
                  >
                    <b-form-input
                      v-model="formData.username"
                      :state="errors.length > 0 ? false : null"
                      placeholder="Entrez le nom complet"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Email -->
              <b-col md="6">
                <b-form-group>
                  <template #label>
                    Email <span class="text-danger">*</span>
                  </template>
                  <validation-provider
                    #default="{ errors }"
                    name="Email"
                    rules="required|email"
                  >
                    <b-form-input
                      v-model="formData.email"
                      type="email"
                      :state="errors.length > 0 ? false : null"
                      placeholder="Entrez l'email"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Phone -->
              <b-col md="6">
                <b-form-group>
                  <template #label>
                    Téléphone <span class="text-danger">*</span>
                  </template>
                  <validation-provider
                    #default="{ errors }"
                    name="Téléphone"
                    rules="required|numeric|length:8"
                  >
                    <b-form-input
                      v-model="formData.phone"
                      :formatter="formatPhone"
                      :state="errors.length > 0 ? false : null"
                      placeholder="Entrez le numéro de téléphone"
                      maxlength="8"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Phone 2 -->
              <b-col md="6">
                <b-form-group label="Téléphone secondaire (optionnel)">
                  <b-form-input
                    v-model="formData.phone2"
                    :formatter="formatPhone"
                    placeholder="Entrez le numéro de téléphone secondaire"
                    maxlength="8"
                  />
                </b-form-group>
              </b-col>

              <!-- Address -->
              <b-col md="6">
                <b-form-group label="Adresse">
                  <b-form-textarea
                    v-model="formData.address"
                    placeholder="Entrez l'adresse"
                    rows="3"
                  />
                </b-form-group>
              </b-col>

              <!-- Car -->
              <b-col md="6">
                <b-form-group label="Véhicule">
                  <v-select
                    v-model="formData.car"
                    :options="carOptions"
                    label="displayName"
                    :reduce="car => car.id"
                    placeholder="Sélectionnez un véhicule (optionnel)"
                    clearable
                  />
                </b-form-group>
              </b-col>

              <!-- Repository -->
              <b-col md="6">
                <b-form-group label="Dépôt">
                  <v-select
                    v-model="formData.repository"
                    :options="repositories"
                    label="name"
                    :reduce="repo => repo.id"
                    placeholder="Sélectionnez un dépôt (optionnel)"
                    clearable
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <h5 class="mt-3 mb-3">
              <feather-icon
                icon="LockIcon"
                class="mr-1"
              />
              Informations d'accès
            </h5>

            <b-row>
              <!-- Password -->
              <b-col md="6">
                <b-form-group>
                  <template #label>
                    Mot de passe <span class="text-danger">*</span>
                  </template>
                  <validation-provider
                    #default="{ errors }"
                    name="Mot de passe"
                    rules="required|min:8"
                    vid="password"
                  >
                    <b-input-group class="input-group-merge">
                      <b-form-input
                        v-model="formData.password"
                        :type="showPassword ? 'text' : 'password'"
                        :state="errors.length > 0 ? false : null"
                        placeholder="Entrez le mot de passe"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          :icon="showPassword ? 'EyeOffIcon' : 'EyeIcon'"
                          class="cursor-pointer"
                          @click="showPassword = !showPassword"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Confirm Password -->
              <b-col md="6">
                <b-form-group>
                  <template #label>
                    Confirmer le mot de passe <span class="text-danger">*</span>
                  </template>
                  <validation-provider
                    #default="{ errors }"
                    name="Confirmation du mot de passe"
                    rules="required|confirmed:password"
                  >
                    <b-input-group class="input-group-merge">
                      <b-form-input
                        v-model="formData.confirm_password"
                        :type="showConfirmPassword ? 'text' : 'password'"
                        :state="errors.length > 0 ? false : null"
                        placeholder="Confirmez le mot de passe"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          :icon="showConfirmPassword ? 'EyeOffIcon' : 'EyeIcon'"
                          class="cursor-pointer"
                          @click="showConfirmPassword = !showConfirmPassword"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>

            <div class="d-flex justify-content-between mt-3">
              <b-button
                variant="outline-secondary"
                :to="{ name: 'pickupeur-list' }"
              >
                <feather-icon
                  icon="XIcon"
                  class="mr-1"
                />
                Annuler
              </b-button>

              <b-button
                variant="primary"
                type="submit"
                :disabled="isSubmitting"
              >
                <b-spinner
                  v-if="isSubmitting"
                  small
                  class="mr-1"
                />
                <feather-icon
                  v-else
                  icon="SaveIcon"
                  class="mr-1"
                />
                Enregistrer
              </b-button>
            </div>
          </b-card>
        </b-form>
      </validation-observer>
    </b-overlay>
  </b-card>
</template>

<script>
import { extend, ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  required, email, numeric, length, min, confirmed,
} from 'vee-validate/dist/rules'
import {
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BRow,
  BCol,
  BButton,
  BInputGroup,
  BInputGroupAppend,
  BSpinner,
  BOverlay,
  BFormTextarea,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from 'axios'

extend('required', required)
extend('email', email)
extend('numeric', numeric)
extend('length', length)
extend('min', min)
extend('confirmed', confirmed)

export default {
  components: {
    BCard,
    ValidationProvider,
    ValidationObserver,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BRow,
    BCol,
    BButton,
    BInputGroup,
    BInputGroupAppend,
    BSpinner,
    BOverlay,
    vSelect,
  },

  data() {
    return {
      isLoading: false,
      isSubmitting: false,
      cars: [],
      repositories: [],
      showPassword: false,
      showConfirmPassword: false,
      formData: {
        username: '',
        email: '',
        phone: '',
        phone2: '',
        address: '',
        car: null,
        repository: null,
        password: '',
        confirm_password: '',
      },
    }
  },

  computed: {
    carOptions() {
      if (!Array.isArray(this.cars)) return []
      return this.cars.map(car => ({
        ...car,
        displayName: `${car.brand || 'N/A'} - ${car.serial_number || 'N/A'} (${car.color || 'N/A'})`,
      }))
    },
  },

  async created() {
    this.isLoading = true
    try {
      await Promise.all([this.fetchCars(), this.fetchRepositories()])
    } finally {
      this.isLoading = false
    }
  },

  methods: {
    formatPhone(value) {
      return value.replace(/\D/g, '').substring(0, 8)
    },

    async fetchRepositories() {
      try {
        const response = await axios.get('/api/repositories/')
        if (Array.isArray(response.data)) {
          this.repositories = response.data
        } else if (response.data && Array.isArray(response.data.results)) {
          this.repositories = response.data.results
        }
      } catch (error) {
        this.repositories = []
        console.error('Error fetching repositories:', error)
      }
    },

    async fetchCars() {
      try {
        const response = await axios.get('/api/cars/')
        let cars = []
        if (Array.isArray(response.data)) {
          cars = response.data
        } else if (response.data && Array.isArray(response.data.results)) {
          cars = response.data.results
        }
        this.cars = cars
      } catch (error) {
        this.cars = []
        this.showToast('danger', 'Erreur lors du chargement des véhicules')
      }
    },

    async submitForm() {
      this.isSubmitting = true

      try {
        const payload = {
          username: this.formData.username,
          email: this.formData.email,
          phone: this.formData.phone,
          phone2: this.formData.phone2,
          address: this.formData.address,
          car: this.formData.car,
          repository: this.formData.repository,
          password: this.formData.password,
        }

        await axios.post('/api/auth/register/pickupeur/', payload)

        this.showToast('success', 'Ramasseur ajouté avec succès')
        this.$router.push({ name: 'pickupeur-list' })
      } catch (error) {
        console.error('Error creating pickupeur:', error)

        let errorMessage = 'Erreur lors de la création du ramasseur'

        if (error.response && error.response.data) {
          const errorData = error.response.data

          if (errorData.email) {
            const rawError = Array.isArray(errorData.email) ? errorData.email[0] : errorData.email
            if (rawError.includes('email') && (rawError.includes('existe') || rawError.includes('already exists'))) {
              errorMessage = 'Ramasseur avec cet email existe déjà'
            } else {
              errorMessage = rawError
            }
          } else if (errorData.phone) {
            const rawError = Array.isArray(errorData.phone) ? errorData.phone[0] : errorData.phone
            if (rawError.includes('phone') && (rawError.includes('existe') || rawError.includes('already exists'))) {
              errorMessage = 'Ramasseur avec ce téléphone existe déjà'
            } else {
              errorMessage = rawError
            }
          } else if (errorData.message) {
            errorMessage = errorData.message
          } else if (errorData.detail) {
            errorMessage = errorData.detail
          }
        }

        this.showToast('danger', errorMessage)
      } finally {
        this.isSubmitting = false
      }
    },

    showToast(variant, text) {
      let title
      let icon

      if (variant === 'success') {
        title = 'Succès'
        icon = 'CheckCircleIcon'
      } else if (variant === 'warning') {
        title = 'Attention'
        icon = 'AlertTriangleIcon'
      } else {
        title = 'Erreur'
        icon = 'XCircleIcon'
      }

      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon,
          text,
          variant,
        },
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.cursor-pointer {
  cursor: pointer;
}
</style>
